import { Color } from '@persona-id/design-tokens';
import { zip } from 'lodash';
import {
  Dispatch,
  RefCallback,
  RefObject,
  SetStateAction,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import DragSelect, {
  calculateIsOverlap,
  SelectionBox,
} from 'dashboard/apps/InquiryTemplateEditor/WorkflowTree/DragSelect';
import {
  WORKFLOW_STEP_CARD_WIDTH,
  WorkflowStepCard,
} from 'dashboard/apps/InquiryTemplateEditor/WorkflowTree/WorkflowStepCard';
import WorkflowStepConditionalPill from 'dashboard/apps/InquiryTemplateEditor/WorkflowTree/WorkflowStepConditionalPill';
import WorkflowTreeAddStepButton from 'dashboard/apps/InquiryTemplateEditor/WorkflowTree/WorkflowTreeAddStepButton';
import { WithCopyable } from 'dashboard/apps/InquiryTemplateEditor/WorkflowTree/WorkflowTreeCopyAndPasteUtils';
import {
  DragLayer,
  WithDraggable,
} from 'dashboard/apps/InquiryTemplateEditor/WorkflowTree/WorkflowTreeDragAndDropUtils';
import StepFlow from 'dashboard/components/StepFlow';
import {
  PathHighlightInformation,
  PathIndex,
} from 'dashboard/components/StepFlow/StepFlowElements';
import { usePanZoomBoxControls } from 'dashboard/components-lib/PanZoomBox';
import { WorkflowRunStepStatsCard } from 'dashboard/components-lib/WorkflowRunStepStats';
import { getTransformationMatrix2DParams } from 'dashboard/lib/utils/cssTransformationMatrix';
import { flattenSteps, getTokensToRunSteps } from 'dashboard/lib/workflows';
import { humanReadableRunTime, startEndTime } from 'dashboard/lib/workflows/run-stats';
import { errorFieldMatchesConditionalBranchIndex } from 'dashboard/lib/workflows/validation';
import { BaseTrigger, Trigger } from 'dashboard/models/trigger';
import { WorkflowError, WorkflowErrorType } from 'dashboard/models/workflow';
import {
  WorkflowRunStep,
  WorkflowRunStepConditional,
  WorkflowRunStepLoop,
  WorkflowRunStepStatus,
} from 'dashboard/models/workflow-step';
import {
  FakeWorkflowStepType,
  outputStep,
  WorkflowStep,
  WorkflowStepAll,
  WorkflowStepInput,
  WorkflowStepLoopType,
  WorkflowStepType,
} from 'dashboard/models/workflow-version';
import Tooltip from 'design-system/components/Tooltip';
import { LocalStorageKey } from 'lib/clients/StorageClient';
import { EVENT_NAME_TO_DESCRIPTION } from 'lib/constants/events';
import useLocalStorage from 'lib/hooks/useLocalStorage';
import { ApiModel } from 'lib/interfaces/api';
import { assertNever } from 'lib/utils/types';
import { TokensToNode } from 'lib/workflows';

export const nodeLabelForTrigger = (triggerStep: Trigger) => {
  switch (triggerStep.type) {
    case ApiModel.TriggerDirect:
      return '';
    case ApiModel.TriggerEvent:
      return `On ${EVENT_NAME_TO_DESCRIPTION[triggerStep.attributes['event-name']]}`;
    case ApiModel.TriggerApi:
      return 'API Trigger';
    case ApiModel.TriggerSchedule:
      return 'On Schedule';
    case ApiModel.TriggerRecurring:
      return 'Recurring';
    case ApiModel.TriggerAccount:
    case ApiModel.TriggerCase:
      return 'On Click';
    case ApiModel.TriggerComputedField:
      return 'Computed Field';
    default:
      assertNever(triggerStep);
  }
};

export interface WorkflowTreeProps {
  inputStep: WorkflowStepInput;
  steps: WorkflowStep[];
  activeNodeToken: string | null;
  editable: boolean;
  selectNode: (selectedNodeToken: string) => void;
  setConditionalRouteToEdit: Dispatch<SetStateAction<number>>;
  handleAddNode: ((previousNodeToken: string) => void) | null;
  handleAddFirstChildNode: ((parentNodeToken: string, branchIndex: number) => void) | null;
  handleCopySteps?: (stepTokens: string[]) => void;
  deactivatedNodeTokens?: string[];
  focusedNodeToken: string | null;
  validationErrors: WorkflowError[] | null;
  moveStep:
    | ((
        targetStepToken: string,
        previousOrParentOfDestinationNodeToken: string,
        destinationBranchIndex?: number,
      ) => void)
    | null;
  workflowRunSteps?: WorkflowRunStep[];
  workflowStepHighlightMap?: Record<string, Color>;
  workflowRunSimulatedAndPending?: boolean;
  disablePan?: boolean;
  nodeGraph: TokensToNode;
  loopTokensToCycleIdx?: Record<string, number>;
  showRunStats?: boolean;
}

const WorkflowTree = ({
  inputStep,
  steps,
  activeNodeToken,
  editable,
  selectNode,
  setConditionalRouteToEdit,
  handleAddNode,
  handleAddFirstChildNode,
  handleCopySteps,
  focusedNodeToken,
  validationErrors,
  deactivatedNodeTokens = [],
  moveStep,
  // only passed in if in workflow-runs view
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  workflowRunSteps = null,
  // only passed in if in version diff
  workflowStepHighlightMap = {},
  workflowRunSimulatedAndPending = false,
  disablePan = false,
  nodeGraph,
  loopTokensToCycleIdx = {},
  showRunStats = false,
}: WorkflowTreeProps) => {
  const [selectedForCopy, setSelectedForCopy] = useState<Record<string, boolean>>({});
  const [isDraggingForCopy, setIsDraggingForCopy] = useState(false);
  const stepRefs = useRef<Record<string, React.RefObject<HTMLDivElement>>>({});

  const tokensToRunSteps = useMemo(
    () => getTokensToRunSteps(loopTokensToCycleIdx, nodeGraph, workflowRunSteps),
    [workflowRunSteps, loopTokensToCycleIdx, nodeGraph],
  );
  const showingWorkflowRun = !!workflowRunSteps && !workflowRunSimulatedAndPending;
  const isDirectTriggerWorkflow = inputStep.trigger?.type === ApiModel.TriggerDirect;
  // omit trigger and output from tree for TriggerDirect workflows
  const allSteps: WorkflowStepAll[] = isDirectTriggerWorkflow
    ? steps
    : [inputStep, ...steps, outputStep];

  const childlessRunNodeTokens = useMemo(
    () =>
      (workflowRunSteps || [])
        .filter((runStep) => runStep.type === ApiModel.WorkflowRunStepLoop)
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        .filter((loopStep: WorkflowRunStepLoop) => {
          switch (loopStep.attributes.settings.type) {
            case WorkflowStepLoopType.ForEach:
            case WorkflowStepLoopType.Map:
              return !loopStep.attributes['resolved-input-array']?.length;
            case WorkflowStepLoopType.While:
              return false;
          }
        })
        .map((loopStep) => loopStep.attributes['config-step-token']),
    [workflowRunSteps],
  );

  useLayoutEffect(() => {
    const triggerNodeElement = document.getElementById(inputStep.token);
    triggerNodeElement && triggerNodeElement.scrollIntoView({ block: 'center', inline: 'center' });
  }, []);

  const {
    attachUndraggable,
    panScreen,
    scrollRef,
    zoomableRef,
    transformationMatrixRef,
    centerTarget,
  } = usePanZoomBoxControls();

  const currentScale = transformationMatrixRef.current
    ? getTransformationMatrix2DParams(transformationMatrixRef.current).scaleX
    : 1;

  useLayoutEffect(() => {
    if (focusedNodeToken) {
      const focusedNodeElement = document.getElementById(focusedNodeToken);
      focusedNodeElement && centerTarget({ dom: focusedNodeElement, scale: currentScale });
    }
  }, [focusedNodeToken, centerTarget, currentScale]);

  const [persistedCollapsedNodeTokens, persistCollapsedNodeTokens] = useLocalStorage<
    Record<string, string[]>
  >(LocalStorageKey.DashboardWorkflowEditorCollapsedSteps, {});
  const collapsedNodeTokens = useMemo(() => {
    let ancestors: string[] = [];

    // Purposefully not touching persistedCollapsedNodeTokens so when activeNodeToken changes,
    // the workflow tree will re-collapse the nodes that the user collapsed before
    if (activeNodeToken) {
      let ancestorToken = nodeGraph[activeNodeToken]?.parent?.step.token;
      while (ancestorToken) {
        ancestors = [ancestorToken, ...ancestors];
        ancestorToken = nodeGraph[ancestorToken]?.parent?.step.token;
      }
    }
    return (
      persistedCollapsedNodeTokens[inputStep.token]?.filter((id) => !ancestors.includes(id)) ?? []
    );
  }, [activeNodeToken, persistedCollapsedNodeTokens, inputStep.token, nodeGraph]);
  const toggleNode = useCallback(
    (targetNodeToken: string) => {
      selectNode(targetNodeToken);
      persistCollapsedNodeTokens({
        ...persistedCollapsedNodeTokens,
        [inputStep.token]:
          collapsedNodeTokens.indexOf(targetNodeToken) === -1
            ? [...collapsedNodeTokens, targetNodeToken]
            : collapsedNodeTokens.filter((token) => token !== targetNodeToken),
      });
    },
    [
      collapsedNodeTokens,
      persistCollapsedNodeTokens,
      persistedCollapsedNodeTokens,
      selectNode,
      inputStep.token,
    ],
  );

  const handleOnMouseUp = useCallback(
    (selectionBox: SelectionBox) => {
      const selectedStepTokens = flattenSteps(steps)
        .filter(
          (step) =>
            stepRefs.current[step.token]?.current &&
            calculateIsOverlap(
              selectionBox,
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              stepRefs.current[step.token].current.getBoundingClientRect(),
            ),
        )
        .flatMap((step) => (collapsedNodeTokens.includes(step.token) ? flattenSteps([step]) : step))
        .map((step) => step.token);
      handleCopySteps?.(selectedStepTokens);
      setTimeout(() => {
        setSelectedForCopy({});
        setIsDraggingForCopy(false);
      }, 400);
    },
    [steps, collapsedNodeTokens, handleCopySteps],
  );

  const handleOnMouseMove = useCallback(
    (selectionBox: SelectionBox) => {
      setIsDraggingForCopy(true);
      const selectedStepTokens = flattenSteps(steps)
        .filter(
          (step) =>
            stepRefs.current[step.token]?.current &&
            calculateIsOverlap(
              selectionBox,
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              stepRefs.current[step.token].current.getBoundingClientRect(),
            ),
        )
        .reduce((result: Record<string, boolean>, step) => {
          result[step.token] = true;
          return result;
        }, {});

      setSelectedForCopy(selectedStepTokens);
    },
    [steps],
  );

  const digParent = (baseStep: WorkflowStepAll): WorkflowStepAll[] => {
    if (nodeGraph[baseStep.token]?.parent) {
      return [baseStep, ...digParent(nodeGraph[baseStep.token].parent.step)];
    } else {
      return [baseStep];
    }
  };

  const renderStep = (currentStep: WorkflowStepAll) => {
    const showStepRunStats =
      showRunStats &&
      tokensToRunSteps[currentStep.token] &&
      ![WorkflowRunStepStatus.Initialized, WorkflowRunStepStatus.Canceled].includes(
        tokensToRunSteps[currentStep.token].attributes.status,
      );
    switch (currentStep.type) {
      case WorkflowStepType.Input: {
        if (!currentStep.trigger || currentStep.trigger.type === ApiModel.TriggerDirect) {
          return null;
        }

        const name = nodeLabelForTrigger(currentStep.trigger);
        return (
          <div
            aria-label={name}
            id={currentStep.token}
            role='button'
            onClick={() => selectNode(currentStep.token)}
          >
            <WorkflowStepCard
              isInitial
              token='trigger'
              type={currentStep.type}
              name={name}
              active={!isDraggingForCopy && currentStep.token === activeNodeToken}
              deactivated={
                deactivatedNodeTokens.includes(currentStep.token) ||
                deactivatedNodeTokens.includes(BaseTrigger)
              }
              focused={currentStep.token === focusedNodeToken}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              hasError={
                validationErrors?.some((error) => error.token === BaseTrigger) ||
                validationErrors?.some((error) => error.token === currentStep.token)
              }
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              workflowRunStepStatus={
                workflowRunSteps != null && workflowRunSteps.length > 0
                  ? WorkflowRunStepStatus.Completed
                  : null
              }
            />
          </div>
        );
      }
      case WorkflowStepType.Output: {
        // if the penultimate step is complete, show output step as complete
        const lastConfigStepToken = steps[steps.length - 1]?.token;
        const lastRunStep = workflowRunSteps?.find(
          (runStep) => runStep?.attributes?.['config-step-token'] === lastConfigStepToken,
        );
        const stepStatus =
          lastRunStep?.attributes?.status === WorkflowRunStepStatus.Completed
            ? WorkflowRunStepStatus.Completed
            : WorkflowRunStepStatus.Initialized;
        return (
          <div
            aria-label={currentStep.name}
            id={currentStep.token}
            role='button'
            onClick={() => selectNode(currentStep.token)}
          >
            <WorkflowStepCard
              active={!isDraggingForCopy && currentStep.token === activeNodeToken}
              data-test='workflow-tree__output-step'
              deactivated={deactivatedNodeTokens.includes(currentStep.token)}
              focused={currentStep.token === focusedNodeToken}
              isLast
              name={currentStep.name}
              token={currentStep.token}
              type={currentStep.type}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              hasError={validationErrors?.some((error) => error.token === outputStep.token)}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              workflowRunStepStatus={lastRunStep ? stepStatus : null}
            />
          </div>
        );
      }
      case FakeWorkflowStepType.MapOutput:
        return (
          <div
            aria-label={currentStep.name}
            id={currentStep.token}
            role='button'
            onClick={() => selectNode(currentStep.token)}
          >
            <WorkflowStepCard
              active={!isDraggingForCopy && currentStep.token === activeNodeToken}
              name={currentStep.name}
              deactivated={deactivatedNodeTokens.includes(currentStep.token)}
              focused={currentStep.token === focusedNodeToken}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              hasError={validationErrors?.some((error) => error.token === currentStep.token)}
              token={currentStep.token}
              type={currentStep.type}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              workflowRunStepStatus={
                showingWorkflowRun
                  ? tokensToRunSteps[nodeGraph[currentStep.token].parent.step.token] // parent step may not exist
                    ? tokensToRunSteps[nodeGraph[currentStep.token].parent.step.token].attributes
                        .status
                    : WorkflowRunStepStatus.Canceled
                  : null
              }
            />
          </div>
        );
      case WorkflowStepType.Action:
      case WorkflowStepType.Wait:
      case WorkflowStepType.Conditional:
      case WorkflowStepType.Module:
      case WorkflowStepType.Loop:
      case WorkflowStepType.Parallel:
      case WorkflowStepType.Placeholder: {
        let durationString: string | null = null;
        if (showingWorkflowRun && showStepRunStats) {
          const { startTime, endTime } = startEndTime([tokensToRunSteps[currentStep.token]]);
          durationString = humanReadableRunTime(startTime, endTime).durationString;
        }
        const stepCard = (
          dragSourceRef?: RefCallback<HTMLDivElement>,
          copyableRef?: RefObject<HTMLDivElement>,
        ) => {
          if (copyableRef) {
            stepRefs.current[currentStep.token] = copyableRef;
          }
          return (
            <div
              aria-label={currentStep.name}
              id={currentStep.token}
              role='button'
              onClick={() => selectNode(currentStep.token)}
            >
              <div ref={copyableRef}>
                <WorkflowStepCard
                  id={`workflow-tree__step-card-${currentStep.token}`}
                  ref={dragSourceRef}
                  token={currentStep.token}
                  type={currentStep.type}
                  isInitial={isDirectTriggerWorkflow && currentStep.token === steps[0].token}
                  active={
                    isDraggingForCopy
                      ? selectedForCopy[currentStep.token]
                      : currentStep.token === activeNodeToken
                  }
                  deactivated={deactivatedNodeTokens.includes(currentStep.token)}
                  focused={currentStep.token === focusedNodeToken}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  hasError={validationErrors?.some((error) => error.token === currentStep.token)}
                  name={currentStep.name}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  workflowRunStepStatus={
                    !showingWorkflowRun
                      ? null
                      : tokensToRunSteps[currentStep.token] // canceled step may not exist
                        ? tokensToRunSteps[currentStep.token].attributes.status
                        : WorkflowRunStepStatus.Canceled
                  }
                  collapsed={collapsedNodeTokens.includes(currentStep.token)}
                  workflowStepHighlightColor={workflowStepHighlightMap?.[currentStep.token] ?? null}
                  onClickCollapseIcon={
                    [
                      WorkflowStepType.Parallel,
                      WorkflowStepType.Conditional,
                      WorkflowStepType.Loop,
                    ].includes(currentStep.type) &&
                    !childlessRunNodeTokens.includes(currentStep.token)
                      ? () => {
                          toggleNode(currentStep.token);
                        }
                      : undefined
                  }
                  duration={durationString}
                />
              </div>
            </div>
          );
        };
        return editable ? (
          <WithDraggable token={currentStep.token} attachUndraggable={attachUndraggable}>
            {(dragSourceRef) => (
              <WithCopyable>{(copyableRef) => stepCard(dragSourceRef, copyableRef)}</WithCopyable>
            )}
          </WithDraggable>
        ) : !showingWorkflowRun ? (
          <WithCopyable>{(copyableRef) => stepCard(undefined, copyableRef)}</WithCopyable>
        ) : showStepRunStats ? (
          <Tooltip trigger={stepCard()}>
            <WorkflowRunStepStatsCard
              runStep={tokensToRunSteps[currentStep.token]}
              stepName={currentStep.name}
            />
          </Tooltip>
        ) : (
          stepCard()
        );
      }
      default:
        assertNever(currentStep);
    }
  };

  const renderConnector = (previousStep: WorkflowStepAll, nextStep: WorkflowStepAll) => {
    const aroundNodes = [previousStep, nextStep].filter((step) => step != null);
    const allParents =
      previousStep && previousStep.type !== WorkflowStepType.Input ? digParent(previousStep) : [];
    const isLast =
      // handles TriggerDirect workflows which don't render output step
      (isDirectTriggerWorkflow && nextStep == null) ||
      (nextStep?.type === WorkflowStepType.Output &&
        (previousStep.type === WorkflowStepType.Input || !nodeGraph[previousStep.token].parent));

    const previousRunStep = previousStep ? tokensToRunSteps[previousStep.token] : null;
    const nextRunStep = nextStep ? tokensToRunSteps[nextStep.token] : null;

    const lineColor = (() => {
      if (showingWorkflowRun && nextStep && nextStep.type === FakeWorkflowStepType.MapOutput) {
        return Color.CorePurple600;
      }
      if (nextRunStep) {
        return nextRunStep.attributes.status !== WorkflowRunStepStatus.Initialized &&
          nextRunStep.attributes.status !== WorkflowRunStepStatus.Canceled
          ? Color.CorePurple600
          : null;
      } else if (previousRunStep) {
        return previousRunStep.attributes.status === WorkflowRunStepStatus.Completed
          ? Color.CorePurple600
          : null;
      }
    })();
    const omitAddStepButton =
      (previousStep.type === WorkflowStepType.Placeholder ||
        previousStep.type === FakeWorkflowStepType.MapOutput) &&
      !isLast;

    return omitAddStepButton ? null : (
      <WorkflowTreeAddStepButton
        aroundNodes={[...aroundNodes, ...allParents]}
        editable={editable}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        handleAddNode={() => handleAddNode(previousStep.token)}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        onDrop={(droppedToken) => moveStep(droppedToken, previousStep.token)}
        isLast={isLast}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        lineColor={lineColor}
      />
    );
  };

  const renderBranchRootConnector = (branchRootStep: WorkflowStep, branchIndex: number) => {
    const stepConditionalCard = () => {
      if (branchRootStep.type === WorkflowStepType.Conditional) {
        /*
         * N.B. Typing the runStep is difficult because WorkflowRunStep and WorkflowSteps are separate
         * WorkflowRunSteps are the actual model created when we run the workflow run and
         * WorkflowStep is the JSON stepsConfig representation.
         */
        const runStep = tokensToRunSteps[branchRootStep.token] as WorkflowRunStepConditional;
        const branchAndConditions = zip(branchRootStep.branches, branchRootStep.conditionals);
        const routeName =
          branchIndex === branchAndConditions.length - 1
            ? 'Else'
            : branchRootStep.conditionals[branchIndex].name || `Route ${branchIndex + 1}`;

        return (
          <WorkflowStepConditionalPill
            active={branchRootStep.token === activeNodeToken}
            title={routeName}
            routeIndex={branchIndex + 1}
            hasError={validationErrors?.some((error) => {
              if (branchRootStep.token !== error.token) return false;
              return error.type === WorkflowErrorType.Run
                ? false
                : errorFieldMatchesConditionalBranchIndex(error.field, branchIndex);
            })}
            wasChosenDuringRun={runStep?.attributes?.['chosen-conditional-index'] === branchIndex}
            showingWorkflowRun={showingWorkflowRun}
            onClickCard={() => {
              selectNode(branchRootStep.token);
              if (editable) {
                setConditionalRouteToEdit(branchIndex + 1);
              }
            }}
          />
        );
      }
    };

    const addButton = () => {
      const previousRunStep = tokensToRunSteps[branchRootStep.token];

      if (branchRootStep.type === WorkflowStepType.Loop) {
        const branchFirstStep = branchRootStep.branch[0];
        const aroundNodes = branchFirstStep
          ? [branchFirstStep, ...digParent(branchRootStep)]
          : [...digParent(branchRootStep)];
        const firstChildRunStep = branchFirstStep ? tokensToRunSteps[branchFirstStep.token] : null;
        const lineColor =
          firstChildRunStep &&
          firstChildRunStep.attributes.status !== WorkflowRunStepStatus.Initialized &&
          firstChildRunStep.attributes.status !== WorkflowRunStepStatus.Canceled
            ? Color.CorePurple600
            : null;
        return (
          <WorkflowTreeAddStepButton
            aroundNodes={aroundNodes}
            editable={editable}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            handleAddNode={() => handleAddFirstChildNode(branchRootStep.token, branchIndex)}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            onDrop={(droppedToken) => moveStep(droppedToken, branchRootStep.token, branchIndex)}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            lineColor={lineColor}
          />
        );
      }

      if (branchRootStep.type === WorkflowStepType.Parallel) {
        const branchFirstStep = branchRootStep.branches[branchIndex][0];
        const aroundNodes = branchFirstStep
          ? [branchFirstStep, ...digParent(branchRootStep)]
          : [...digParent(branchRootStep)];

        const lineColor =
          previousRunStep &&
          previousRunStep.attributes.status !== WorkflowRunStepStatus.Initialized &&
          previousRunStep.attributes.status !== WorkflowRunStepStatus.Canceled &&
          previousRunStep.attributes.status !== WorkflowRunStepStatus.Errored
            ? Color.CorePurple600
            : null;

        return (
          <WorkflowTreeAddStepButton
            aroundNodes={aroundNodes}
            editable={editable}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            handleAddNode={() => handleAddFirstChildNode(branchRootStep.token, branchIndex)}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            onDrop={(droppedToken) => moveStep(droppedToken, branchRootStep.token, branchIndex)}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            lineColor={lineColor}
          />
        );
      }

      if (branchRootStep.type === WorkflowStepType.Conditional) {
        const branchFirstStep = branchRootStep.branches[branchIndex][0];
        const aroundNodes = branchFirstStep
          ? [branchFirstStep, ...digParent(branchRootStep)]
          : [...digParent(branchRootStep)];

        const runStep = tokensToRunSteps[branchRootStep.token] as WorkflowRunStepConditional;

        const lineColor =
          runStep?.attributes?.['chosen-conditional-index'] === branchIndex
            ? Color.CorePurple600
            : null;
        return (
          <WorkflowTreeAddStepButton
            aroundNodes={aroundNodes}
            editable={editable}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            handleAddNode={() => handleAddFirstChildNode(branchRootStep.token, branchIndex)}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            onDrop={(droppedToken) => moveStep(droppedToken, branchRootStep.token, branchIndex)}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            lineColor={lineColor}
          />
        );
      }
    };

    return (
      <>
        {stepConditionalCard()}
        {addButton()}
      </>
    );
  };

  const pathHighlightInformationForStep = (
    currentStep: WorkflowStepAll,
  ): PathHighlightInformation => {
    if (showingWorkflowRun && currentStep.type === FakeWorkflowStepType.MapOutput) {
      return {
        connectorAfterStepColor: Color.CorePurple600,
        toOutBranchLineColor: Color.CorePurple600,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        leftBranchOutConnectorsColor: null,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        rightBranchOutConnectorsColor: null,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        rightBranchInConnectorsColor: null,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        leftBranchInConnectorsColor: null,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        pathToChildOutIndex: null,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        pathToChildInIndex: null,
      };
    }

    const currentRunStep = currentStep ? tokensToRunSteps[currentStep.token] : null;

    if (currentRunStep) {
      let toOutBranchLineColor =
        currentRunStep.attributes.status !== WorkflowRunStepStatus.Initialized
          ? Color.CorePurple600
          : null;

      let connectorAfterStepColor =
        currentRunStep.attributes.status === WorkflowRunStepStatus.Completed
          ? Color.CorePurple600
          : null;

      let pathToChildIndex = PathIndex.None;

      if (currentStep.type === WorkflowStepType.Conditional) {
        pathToChildIndex = (currentRunStep as WorkflowRunStepConditional)?.attributes?.[
          'chosen-conditional-index'
        ];
      }

      if (currentStep.type === WorkflowStepType.Parallel) {
        pathToChildIndex = PathIndex.All;
      }

      // for use with parallel and loop
      let leftBranchOutConnectorsColor;
      let rightBranchOutConnectorsColor;
      let rightBranchInConnectorsColor;
      let leftBranchInConnectorsColor;

      if (currentRunStep && currentStep.type === WorkflowStepType.Parallel) {
        if (
          currentRunStep.attributes.status !== WorkflowRunStepStatus.Initialized &&
          currentRunStep.attributes.status !== WorkflowRunStepStatus.Canceled
        ) {
          leftBranchOutConnectorsColor = Color.CorePurple600;
          rightBranchOutConnectorsColor = Color.CorePurple600;
        }

        if (currentRunStep.attributes.status === WorkflowRunStepStatus.Completed) {
          leftBranchInConnectorsColor = Color.CorePurple600;
          rightBranchInConnectorsColor = Color.CorePurple600;
        }
      }

      if (currentRunStep && currentStep.type === WorkflowStepType.Loop) {
        toOutBranchLineColor = null;
        connectorAfterStepColor = null;
        const firstChildRunStep = currentStep.branch?.length
          ? tokensToRunSteps[currentStep.branch[0].token]
          : null;
        if (
          firstChildRunStep &&
          firstChildRunStep.attributes.status !== WorkflowRunStepStatus.Initialized &&
          firstChildRunStep.attributes.status !== WorkflowRunStepStatus.Canceled
        ) {
          connectorAfterStepColor = Color.CorePurple600;
          toOutBranchLineColor = Color.CorePurple600;
          rightBranchOutConnectorsColor = Color.CorePurple600;
        }

        const lastChildRunStep = currentStep.branch?.length
          ? currentStep.settings.type === WorkflowStepLoopType.Map
            ? tokensToRunSteps[currentStep.branch[currentStep.branch.length - 2].token]
            : tokensToRunSteps[currentStep.branch[currentStep.branch.length - 1].token]
          : null;
        if (
          lastChildRunStep &&
          lastChildRunStep.attributes.status === WorkflowRunStepStatus.Completed
        ) {
          leftBranchOutConnectorsColor = Color.CorePurple600;
          leftBranchInConnectorsColor = Color.CorePurple600;
          rightBranchInConnectorsColor = Color.CorePurple600;
        }
      }

      return {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        connectorAfterStepColor,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        toOutBranchLineColor,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        leftBranchOutConnectorsColor,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        rightBranchOutConnectorsColor,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        rightBranchInConnectorsColor,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        leftBranchInConnectorsColor,
        pathToChildOutIndex: pathToChildIndex,
        pathToChildInIndex: connectorAfterStepColor ? pathToChildIndex : -1,
      };
    } else {
      return {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        connectorAfterStepColor: null,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        toOutBranchLineColor: null,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        leftBranchOutConnectorsColor: null,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        rightBranchOutConnectorsColor: null,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        rightBranchInConnectorsColor: null,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        leftBranchInConnectorsColor: null,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        pathToChildOutIndex: null,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        pathToChildInIndex: null,
      };
    }
  };

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [draggingItemToken, setDraggingItemToken] = useState<string>(null);
  const blurBranchContainer = (step: WorkflowStep) => draggingItemToken === step.token;

  return (
    <>
      <DragLayer
        containerRef={scrollRef}
        currentScale={currentScale}
        tokensToNode={nodeGraph}
        onStartDragging={setDraggingItemToken}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        onEndDragging={() => setDraggingItemToken(null)}
        panScreen={panScreen}
      />
      <DragSelect handleOnMouseMove={handleOnMouseMove} handleOnMouseUp={handleOnMouseUp} />
      <StepFlow
        collapsedNodeTokens={collapsedNodeTokens}
        childlessRunNodeTokens={childlessRunNodeTokens}
        innerRef={disablePan ? null : zoomableRef}
        steps={allSteps}
        stepContentWidth={WORKFLOW_STEP_CARD_WIDTH}
        renderStep={renderStep}
        renderConnector={renderConnector}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        renderBranchRootConnector={renderBranchRootConnector}
        innerStyle={{
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          blurBranchContainer,
          pathHighlightInformationForStep,
        }}
      />
    </>
  );
};

export default WorkflowTree;
